import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Check,
    HelpCircle,
    Hammer,
    Building2,
    Truck,
    Route,
    ShieldCheck,
    Layers,
} from "lucide-react";
import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationInternalLinks from "@/components/marketing/PrestationInternalLinks";
import ContactForm from "@/components/marketing/ContactForm";
import { SERVICES } from "@/lib/site";
import { buildBreadcrumb, buildService, buildFaqPage, SITE_ORIGIN } from "@/lib/schema";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const SLUG = "demolition-deconstruction";

const PRESTATIONS = [
    {
        icon: <Hammer className="w-5 h-5" />,
        title: "Démolition partielle",
        text: "Nous intervenons pour des démolitions ciblées dans le cadre d’une transformation, d’un réaménagement, d’une rénovation ou d’une préparation de terrain. Ces travaux peuvent concerner des murs, dalles, murets, ouvrages béton, éléments extérieurs ou parties d’ouvrages.",
        bullets: [
            "Démolition de murs",
            "Démolition de dalles",
            "Retrait d’ouvrages béton",
            "Démolition d’éléments extérieurs",
            "Préparation avant réaménagement",
        ],
    },
    {
        icon: <Building2 className="w-5 h-5" />,
        title: "Déconstruction d’ouvrages",
        text: "La déconstruction consiste à démonter ou retirer progressivement des éléments existants afin de préparer le site pour la suite du projet. Cette approche permet d’organiser les travaux, de limiter les impacts sur l’environnement immédiat et de mieux gérer les matériaux.",
        bullets: [
            "Déconstruction partielle",
            "Retrait d’éléments existants",
            "Préparation avant reconstruction",
            "Intervention sur ouvrages extérieurs",
            "Coordination avec les étapes suivantes",
        ],
    },
    {
        icon: <Truck className="w-5 h-5" />,
        title: "Évacuation des gravats et matériaux",
        text: "Les gravats, bétons, déblais et matériaux issus de la démolition doivent être triés, transportés et orientés vers des filières adaptées selon leur nature. L’organisation de cette évacuation dépend des volumes, de l’accès au chantier et des exigences du projet.",
        bullets: [
            "Évacuation des gravats",
            "Tri des matériaux selon leur nature",
            "Gestion des déblais",
            "Transport et organisation logistique",
            "Adaptation en cas de matériaux particuliers ou non prévus",
        ],
    },
    {
        icon: <Layers className="w-5 h-5" />,
        title: "Préparation avant terrassement ou reconstruction",
        text: "Après une démolition, le chantier doit souvent être préparé pour accueillir la suite des travaux : terrassement, fondations, accès, plateforme, enrobés ou aménagements extérieurs. RCPS Terrassement peut assurer la continuité entre la démolition, l’évacuation et la préparation du terrain.",
        bullets: [
            "Préparation de la zone",
            "Remise en ordre selon les prestations prévues",
            "Coordination avec le terrassement",
            "Préparation avant accès ou parking",
            "Préparation avant aménagement extérieur",
        ],
    },
];

const PROJECT_TYPES = [
    "Démolition partielle avant rénovation",
    "Démolition avant terrassement",
    "Déconstruction d’ouvrages extérieurs",
    "Retrait de murs ou murets",
    "Démolition de dalles",
    "Retrait d’éléments béton",
    "Préparation avant construction",
    "Préparation avant aménagement extérieur",
    "Évacuation de gravats",
    "Remise en ordre de zone d’intervention",
    "Travaux pour villas, immeubles, régies ou entreprises",
    "Interventions pour architectes, promoteurs et entreprises générales",
];

const METHODE = [
    {
        title: "Analyse de l’ouvrage",
        text: "Nous étudions le type d’ouvrage à démolir, son environnement, les accès disponibles, les contraintes de sécurité, les volumes à évacuer et les éventuels éléments à préserver.",
        bullets: [
            "Type d’ouvrage",
            "Accès au chantier",
            "Contraintes de voisinage",
            "Volumes estimés",
        ],
    },
    {
        title: "Préparation de l’intervention",
        text: "L’intervention est organisée selon les prestations prévues, la configuration du site, les matériaux concernés, les accès, les moyens nécessaires et la suite du chantier.",
        bullets: [
            "Organisation des travaux",
            "Sécurisation des accès",
            "Protection des abords",
            "Coordination des étapes",
        ],
    },
    {
        title: "Démolition ou déconstruction",
        text: "Les travaux sont réalisés selon la nature de l’ouvrage et les conditions constatées sur place, avec une attention particulière portée à la sécurité, aux matériaux et aux contraintes du site.",
        bullets: [
            "Démolition partielle",
            "Retrait d’éléments béton",
            "Déconstruction d’ouvrages",
            "Préparation de la zone",
        ],
    },
    {
        title: "Évacuation et remise en ordre",
        text: "Les gravats et matériaux issus du chantier sont organisés et évacués selon leur nature. La zone est remise en ordre selon les prestations prévues et les besoins de la suite du projet.",
        bullets: [
            "Évacuation des gravats",
            "Tri selon la nature des matériaux",
            "Organisation logistique",
            "Préparation pour la suite",
        ],
    },
];

const VIGILANCE = [
    {
        icon: <ShieldCheck className="w-5 h-5" />,
        title: "Sécurité du chantier",
        text: "La sécurité des personnes, des accès, des ouvrages voisins et de l’environnement immédiat est prise en compte dès la préparation de l’intervention.",
    },
    {
        icon: <Route className="w-5 h-5" />,
        title: "Voisinage et accès",
        text: "Les démolitions en zone habitée ou urbaine doivent tenir compte des accès, des horaires, des nuisances potentielles, de la circulation et de la proximité des bâtiments voisins.",
    },
    {
        icon: <Truck className="w-5 h-5" />,
        title: "Gestion des matériaux",
        text: "Les gravats, bétons, terres et matériaux retirés doivent être organisés selon leur nature, les volumes concernés et les filières appropriées.",
    },
];

const POURQUOI = [
    {
        title: "Analyse du chantier",
        text: "Chaque demande est étudiée selon la nature de l’ouvrage, les accès, les matériaux, les volumes à évacuer et les contraintes du site.",
    },
    {
        title: "Sécurité prise en compte",
        text: "L’intervention est préparée en tenant compte des personnes, des accès, des ouvrages existants, des réseaux connus et de l’environnement immédiat.",
    },
    {
        title: "Travaux structurés",
        text: "Les étapes de démolition ou déconstruction sont organisées afin de faciliter la suite du chantier : terrassement, reconstruction, accès ou aménagement extérieur.",
    },
    {
        title: "Gestion des gravats",
        text: "Les gravats et matériaux issus du chantier sont pris en compte dans l’analyse, l’organisation et l’exécution des travaux.",
    },
    {
        title: "Intervention locale",
        text: "Basés à Plan-les-Ouates, nous intervenons à Genève, dans le canton de Genève et dans toute la Suisse romande.",
    },
    {
        title: "Solutions adaptées",
        text: "Chaque projet reçoit une approche adaptée à l’ouvrage, à la configuration du site, aux contraintes logistiques et aux besoins du client.",
    },
];

const FAQS = [
    {
        question: "Quelle est la différence entre démolition et déconstruction ?",
        answer: "La démolition consiste à retirer ou détruire un ouvrage ou une partie d’ouvrage. La déconstruction est généralement plus progressive : elle vise à démonter certains éléments, organiser les matériaux et préparer le site pour la suite du projet. Le choix dépend de l’ouvrage, de la configuration du chantier et des objectifs du client.",
    },
    {
        question: "Comment gérez-vous la sécurité et le voisinage pendant la démolition ?",
        answer: "Chaque chantier est étudié selon son environnement, les accès disponibles, les bâtiments voisins, les ouvrages existants et les contraintes de circulation. L’intervention est organisée afin de limiter les risques et de tenir compte de l’environnement immédiat du chantier.",
    },
    {
        question: "Où vont les gravats et matériaux évacués ?",
        answer: "Les gravats et matériaux issus de la démolition sont triés et orientés vers des filières adaptées selon leur nature. Les volumes, l’accès au chantier et les éventuels matériaux particuliers peuvent influencer l’organisation de l’évacuation.",
    },
    {
        question: "Faut-il une autorisation pour démolir un bâtiment à Genève ?",
        answer: "Cela dépend du type d’ouvrage, de la commune, du site concerné, de l’ampleur des travaux et de la situation du bâtiment. Certains projets de démolition ou transformation peuvent nécessiter une autorisation ou des démarches particulières. La situation doit être vérifiée au cas par cas.",
    },
    {
        question: "Peut-on démolir uniquement une partie d’un ouvrage ?",
        answer: "Oui, une démolition partielle peut être envisagée lorsqu’il s’agit de retirer un mur, une dalle, un élément béton, un muret, une structure extérieure ou une partie d’ouvrage. La faisabilité dépend de la configuration du site, de la stabilité de l’existant et des contraintes du chantier.",
    },
    {
        question: "Combien de temps dure une démolition ?",
        answer: "La durée dépend de la taille de l’ouvrage, de l’accès au chantier, des matériaux, des volumes à évacuer, de la sécurité à mettre en place et des contraintes du site. Une visite ou une analyse préalable permet d’estimer plus précisément l’organisation nécessaire.",
    },
];

export default function DemolitionPage() {
    const otherServices = SERVICES.filter((s) => s.slug !== SLUG);

    return (
        <>
            <Seo
                title="Démolition Genève | Déconstruction et évacuation des gravats"
                description="RCPS Terrassement Sàrl réalise vos travaux de démolition et déconstruction à Genève et en Suisse romande : démolition partielle, retrait d’ouvrages béton, évacuation des gravats et préparation de chantier."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Prestations", path: "/prestations" },
                    { name: "Démolition & déconstruction", path: "/prestations/demolition-deconstruction" },
                ])}
                id="breadcrumb-demolition"
            />
            <JsonLd
                data={buildService({
                    slug: "demolition-deconstruction",
                    name: "Démolition et déconstruction à Genève et en Suisse romande",
                    serviceType: "Démolition, déconstruction et évacuation des gravats",
                    description:
                        "Travaux de démolition et déconstruction pour particuliers et professionnels : démolition partielle, retrait d’ouvrages béton, tri et évacuation des gravats, préparation de chantier à Genève et en Suisse romande.",
                    image: `${SITE_ORIGIN}/rcps-assets/prestation-demolition-01.jpeg`,
                })}
                id="service-demolition"
            />
            <JsonLd
                data={buildFaqPage(
                    `${SITE_ORIGIN}/prestations/demolition-deconstruction`,
                    FAQS
                )}
                id="faq-demolition"
            />

            {/* Hero */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="demolition-hero"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/49pwgc58_De%CC%81molition%20-%20Grande%20-%20Grande.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <Reveal>
                            <Breadcrumbs
                                variant="onImage"
                                trail={[
                                    { name: "Accueil", path: "/" },
                                    { name: "Prestations", path: "/prestations" },
                                    { name: "Démolition & déconstruction", path: "/prestations/demolition-deconstruction" },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="kicker text-white/80 mt-5">
                                <span className="hairline bg-white/60" /> Prestation · Démolition & déconstruction
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="mt-5 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                                data-testid="demolition-h1"
                            >
                                Démolition et déconstruction à Genève et en Suisse romande
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed">
                                RCPS Terrassement Sàrl intervient pour les travaux de démolition et
                                de déconstruction à Genève, dans l’ensemble du canton de Genève et
                                dans toute la Suisse romande. Nous accompagnons les particuliers,
                                architectes, régies, promoteurs, entreprises générales, collectivités
                                et professionnels dans la préparation de leurs chantiers.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-4 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                                Démolition partielle, retrait d’ouvrages béton, déconstruction
                                d’éléments existants, évacuation des gravats ou préparation avant
                                terrassement : chaque intervention est étudiée selon la configuration
                                du site, les accès, les contraintes de sécurité, les matériaux à
                                évacuer et la suite du projet.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Reveal delay={0.25}>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-devis"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-navy-700 font-semibold hover:bg-concrete-50 transition"
                            >
                                Demander un devis <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="bg-white" data-testid="demolition-intro">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-6">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Démolition à Genève
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Préparer un chantier avec méthode et sécurité.
                            </h2>
                        </Reveal>
                        <div className="mt-8 space-y-5 text-navy-700/85 text-base md:text-lg leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>La démolition est une étape sensible d’un projet de transformation, rénovation, reconstruction ou aménagement extérieur. Elle demande une préparation sérieuse afin de limiter les risques, organiser les accès, protéger l’environnement immédiat du chantier et gérer correctement les matériaux issus des travaux.</p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>À Genève comme en Suisse romande, un chantier de démolition peut impliquer des contraintes particulières : accès urbains, voisinage, ouvrages existants, réseaux, bruit, poussières, évacuation des gravats, autorisations éventuelles ou coordination avec d’autres intervenants. RCPS Terrassement analyse ces éléments afin de proposer une intervention adaptée à la réalité du chantier.</p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[4/3]">
                                <img src="/rcps-assets/prestation-demolition-01.jpeg" alt="Chantier de démolition RCPS" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Prestations */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="demolition-prestations"
            >
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Nos travaux de démolition
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Des interventions adaptées à chaque ouvrage.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {PRESTATIONS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.05}>
                                <article
                                    className="h-full bg-white rounded-md border border-concrete-200 p-7 md:p-8"
                                    data-testid={`prestation-card-${i + 1}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center flex-shrink-0">
                                            {p.icon}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-mono-num text-xs text-steel">
                                                {String(i + 1).padStart(2, "0")}
                                            </p>
                                            <h3 className="mt-1 font-heading text-xl md:text-2xl font-semibold text-navy-700 tracking-tight">
                                                {p.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="mt-5 text-navy-700/80 leading-relaxed">{p.text}</p>
                                    <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                                        {p.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-navy-700 text-sm"
                                            >
                                                <span className="mt-1.5 w-3.5 h-3.5 rounded-full bg-concrete-100 flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                                </span>
                                                <span className="leading-relaxed">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Types de projets */}
            <section className="bg-white" data-testid="demolition-project-types">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Pour quels projets ?
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Démolition pour particuliers, professionnels et collectivités.
                            </h2>
                            <p className="mt-6 text-navy-700/80 leading-relaxed">
                                RCPS Terrassement intervient sur des projets de démolition et
                                déconstruction de différentes tailles à Genève et en Suisse romande.
                                Chaque demande est étudiée selon la nature de l’ouvrage, l’accès au
                                chantier, les matériaux à retirer, les contraintes de voisinage et
                                les travaux prévus ensuite.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="mb-5 aspect-[16/10] rounded-md overflow-hidden border border-concrete-200">
                                <img src="/rcps-assets/prestation-demolition-02.jpeg" alt="Chantier de démolition RCPS" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {PROJECT_TYPES.map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 bg-concrete-50 border border-concrete-200 rounded-md px-4 py-3 text-navy-700 text-sm md:text-base leading-snug"
                                    >
                                        <span className="mt-1 w-4 h-4 rounded-full bg-white border border-concrete-200 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                        </span>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Méthode */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="demolition-methode"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Notre méthode
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                    Une démolition préparée, organisée et suivie.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {METHODE.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`methode-step-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        Étape {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg md:text-xl font-semibold">
                                        {m.title}
                                    </h3>
                                    <p className="mt-3 text-white/75 text-sm leading-relaxed">
                                        {m.text}
                                    </p>
                                    <ul className="mt-4 space-y-1.5">
                                        {m.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-white/75 text-sm"
                                            >
                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                                                <span className="leading-snug">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Points de vigilance */}
            <InlineCTA
                variant="navy"
                title="Un projet de démolition ou de déconstruction ?"
                text="Nous étudions votre chantier — protection du voisinage, tri des matériaux, contraintes de site — puis vous répondons rapidement."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="demolition-inline-cta"
            />
            <section className="bg-white" data-testid="demolition-vigilance">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
                        <div className="md:col-span-5">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> À prendre en compte
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-[1.15]">
                                    Démolition à Genève : sécurité, voisinage et gestion des gravats.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7 space-y-5 text-navy-700/80 leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>
                                    Un chantier de démolition peut nécessiter une attention
                                    particulière aux accès, au voisinage, au bruit, aux poussières,
                                    aux ouvrages existants, aux réseaux, aux matériaux à évacuer et
                                    aux éventuelles démarches administratives. Selon le type de
                                    projet, la commune, le site concerné et l’ampleur des travaux,
                                    certaines autorisations peuvent être nécessaires.
                                </p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>
                                    RCPS Terrassement étudie chaque demande afin d’anticiper les
                                    contraintes techniques, logistiques et organisationnelles liées
                                    au chantier. Cette analyse permet de préparer une intervention
                                    adaptée et de faciliter la suite des travaux.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.16}>
                        <div className="mb-10 md:mb-12 aspect-[21/9] rounded-md overflow-hidden border border-concrete-200">
                            <img
                                src="/rcps-assets/prestation-demolition-03.jpeg"
                                alt="Chantier de démolition RCPS — protection du voisinage et tri des matériaux"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VIGILANCE.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-concrete-200 bg-concrete-50 p-6 md:p-7"
                                    data-testid={`vigilance-card-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-md bg-white text-navy-700 border border-concrete-200 flex items-center justify-center">
                                        {v.icon}
                                    </div>
                                    <p className="mt-5 font-mono-num text-xs text-steel">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-1 font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-navy-700/75 text-sm md:text-base leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pourquoi RCPS */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="demolition-pourquoi"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Pourquoi RCPS ?
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                                    Une démolition organisée, sécurisée et adaptée au projet.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="mb-10 aspect-[21/9] rounded-md overflow-hidden border border-white/12">
                            <img
                                src="/rcps-assets/prestation-demolition-04.jpeg"
                                alt="Démolition et déconstruction RCPS — méthode et organisation"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {POURQUOI.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`pourquoi-card-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg font-semibold">
                                        {h.title}
                                    </h3>
                                    <p className="mt-2 text-white/75 text-sm leading-relaxed">
                                        {h.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact / Formulaire */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="demolition-contact"
            >
                <div className="rcps-container py-20 md:py-24 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Contact
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 leading-tight tracking-tight">
                                Parlons de votre projet de démolition.
                            </h2>
                            <p className="mt-5 text-navy-700/80 leading-relaxed">
                                Vous avez un ouvrage à démolir, un mur à retirer, une dalle à
                                casser, des gravats à évacuer ou un chantier à préparer avant
                                terrassement ? Transmettez-nous les informations principales. RCPS
                                Terrassement vous répondra après analyse de votre demande.
                            </p>
                            <ul className="mt-6 space-y-2 text-navy-700/80 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Analyse selon la nature de l’ouvrage et les accès</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Prise en compte du voisinage et des contraintes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Réponse après étude de votre demande</span>
                                </li>
                            </ul>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.08}>
                            <ContactForm defaultProjectType="Démolition & déconstruction" />
                            <p className="mt-4 text-xs md:text-sm text-navy-700/60 leading-relaxed">
                                Réponse après analyse de votre demande · devis personnalisé · sans
                                engagement.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="bg-white border-t border-concrete-200"
                data-testid="demolition-faq"
            >
                <div className="rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                        <div className="md:col-span-5 md:sticky md:top-28">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Questions fréquentes
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-tight">
                                    Vos questions sur la démolition et la déconstruction.
                                </h2>
                                <p className="mt-5 text-navy-700/80 leading-relaxed">
                                    Vous avez une question sur un projet de démolition à Genève ou en
                                    Suisse romande ? Contactez-nous pour nous présenter l’ouvrage
                                    concerné, la localisation du chantier, les accès et les travaux
                                    souhaités.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-navy-700">
                                    <span className="w-10 h-10 rounded-md bg-concrete-100 flex items-center justify-center">
                                        <HelpCircle className="w-5 h-5" />
                                    </span>
                                    <Link
                                        to="/contact"
                                        data-testid="faq-contact-link"
                                        className="text-sm font-semibold link-underline"
                                    >
                                        Poser une question à RCPS
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7">
                            <Reveal delay={0.08}>
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="w-full space-y-3"
                                    data-testid="demolition-faq-accordion"
                                >
                                    {FAQS.map((f, i) => (
                                        <AccordionItem
                                            key={f.question}
                                            value={`item-${i}`}
                                            className="border border-concrete-200 rounded-md bg-white overflow-hidden [&[data-state=open]]:border-navy-700 transition"
                                        >
                                            <AccordionTrigger
                                                className="px-5 py-4 md:px-6 md:py-5 text-left hover:no-underline group"
                                                data-testid={`faq-question-${i}`}
                                            >
                                                <div className="flex items-start gap-4 pr-2">
                                                    <span className="font-mono-num text-xs text-navy-700/50 mt-1 flex-shrink-0">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-heading text-navy-700 font-semibold text-base md:text-lg leading-snug">
                                                        {f.question}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-5 md:px-6 pb-5 md:pb-6">
                                                <div className="pl-9 text-navy-700/80 text-sm md:text-base leading-[1.75]">
                                                    {f.answer}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <PrestationInternalLinks
                currentSlug={SLUG}
                relatedCategories={[
                    "Terrassement",
                    "Aménagements extérieurs & enrochement",
                    "Génie civil, voirie & enrobés",
                ]}
                bridgeText={
                    <>
                        <p>
                            Une démolition se prolonge presque toujours par une phase de préparation du
                            terrain et parfois par un aménagement complet du site. RCPS Terrassement
                            accompagne l’ensemble de ces étapes sur un même chantier.
                        </p>
                        <p>
                            Après la déconstruction, nous réalisons également les{" "}
                            <Link
                                to="/prestations/terrassement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                travaux de terrassement à Genève et en Suisse romande
                            </Link>
                            {" "}: décaissement, nivellement, préparation des fondations et évacuation
                            des terres.
                        </p>
                        <p>
                            Si le projet inclut ensuite la création d’accès, de plateformes ou de
                            revêtements, découvrez nos prestations de{" "}
                            <Link
                                to="/prestations/genie-civil-voirie-enrobes"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                génie civil, voirie et enrobés à Genève
                            </Link>
                            . Pour la finalisation d’un espace extérieur — talus, soutènements,
                            enrochement —, nous intervenons également en{" "}
                            <Link
                                to="/prestations/amenagements-exterieurs-enrochement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                aménagements extérieurs et enrochement en Suisse romande
                            </Link>
                            .
                        </p>
                    </>
                }
            />

            {/* CTA final */}
            <SectionCTA
                title="Un projet de démolition à concrétiser ?"
                text="Présentez-nous votre chantier. Nous étudions votre demande de démolition ou déconstruction à Genève ou en Suisse romande selon la localisation, les accès, la nature de l’ouvrage, les matériaux et les contraintes techniques."
            />
        </>
    );
}
